import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Question, ShoppingBag } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Step 3 of 4: confirm ownership status for each material in the AI draft.
 * No project record exists yet. State lives in sessionStorage.halfjob_draft.
 */
const OPTIONS = [
  { value: "have", label: "Have", icon: CheckCircle, color: "ok" },
  { value: "need_to_buy", label: "Need to buy", icon: ShoppingBag, color: "masking" },
  { value: "unsure", label: "Unsure", icon: Question, color: "denim" },
];

export default function ProjectMaterialsConfirmPage() {
  const [draft, setDraft] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const raw = sessionStorage.getItem("halfjob_draft");
    if (!raw) {
      navigate("/projects/new", { replace: true });
      return;
    }
    const d = JSON.parse(raw);
    // Default every material to need_to_buy if not already set.
    const materials = (d.materials || []).map((m) => ({
      ...m,
      user_status: m.user_status || "need_to_buy",
    }));
    setDraft({ ...d, materials });
  }, [navigate]);

  if (!draft) return null;

  const setStatus = (idx, value) => {
    setDraft((d) => ({
      ...d,
      materials: d.materials.map((m, i) => (i === idx ? { ...m, user_status: value } : m)),
    }));
  };

  const next = () => {
    sessionStorage.setItem("halfjob_draft", JSON.stringify(draft));
    navigate("/projects/new/final-review");
  };

  const counts = draft.materials.reduce(
    (acc, m) => ({ ...acc, [m.user_status]: (acc[m.user_status] || 0) + 1 }),
    {},
  );

  return (
    <div className="min-h-screen pb-32" data-testid="project-materials-confirm-page">
      <header className="px-5 pt-6 pb-3 max-w-md mx-auto w-full">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-timber-700 font-bold" data-testid="materials-back">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>
        <p className="label-cap mt-3">Step 3 of 4 · Materials confirm</p>
        <h1 className="font-heading text-2xl font-bold mt-1">What do you already have?</h1>
        <p className="text-sm text-timber-700 mt-1">
          Mark each item so we know what to add to your shopping list.
        </p>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-3">
        {draft.materials.length === 0 && (
          <div className="tactile-card p-6 text-center text-timber-700">
            <p className="text-sm">The AI didn't propose any materials. You can continue and the next step will check for anything that's missing.</p>
          </div>
        )}
        {draft.materials.map((m, i) => (
          <div key={i} className="tactile-card p-3" data-testid={`materials-row-${i}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-timber-900">
                  {m.name}
                  {m.quantity && <span className="text-timber-500 font-normal"> · {m.quantity}</span>}
                </p>
                {m.required && (
                  <span className="inline-block mt-0.5 text-[10px] font-bold uppercase tracking-wider bg-block text-white rounded-full px-1.5 py-0.5">
                    Required
                  </span>
                )}
                {m.explanation && (
                  <p className="text-xs text-timber-700 italic mt-1">{m.explanation}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5 mt-2">
              {OPTIONS.map((o) => {
                const active = m.user_status === o.value;
                return (
                  <button
                    key={o.value}
                    type="button"
                    data-testid={`materials-row-${i}-${o.value}`}
                    onClick={() => setStatus(i, o.value)}
                    className={`min-h-[44px] rounded-lg border-2 px-2 py-1 text-xs font-bold flex flex-col items-center justify-center gap-0.5 transition ${
                      active
                        ? "bg-masking text-timber-900 border-masking shadow-tactile-orange"
                        : "bg-chalk text-timber-700 border-stone_border hover:border-masking"
                    }`}
                  >
                    <o.icon size={16} weight={active ? "fill" : "regular"} />
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div className="bg-kraft border-2 border-stone_border rounded-xl p-3 text-xs text-timber-700" data-testid="materials-counts">
          <p>
            <strong className="text-timber-900">{counts.have || 0}</strong> have ·{" "}
            <strong className="text-timber-900">{counts.need_to_buy || 0}</strong> to buy ·{" "}
            <strong className="text-timber-900">{counts.unsure || 0}</strong> unsure
          </p>
        </div>

        <Button
          data-testid="materials-confirm-next"
          onClick={next}
          className="w-full bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[64px] text-lg font-bold btn-press"
        >
          Run Final Materials Review →
        </Button>
      </main>
    </div>
  );
}
